import React, { ReactElement, useContext } from 'react'

import Heading from '../../Common/Components/Heading'
import Fieldset from '../../Common/Components/Fieldset'
import P from '../../Common/Components/P'
import Checkbox from '../../Common/Components/Checkbox'
import FormRow from '../../Common/Components/FormRow'

import { LanguageContext } from '../../Common/Contexts'

import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { DeviceFiltering as DeviceFilteringLanguage } from '../../../../i18n'
import { HeadingLevel } from '../../Common/Components/Heading/Heading'
import { HelpTagTitles, Tags } from '../../Common/API/Contentful/Constants'
import { HelpTag } from '../../Common/Components/HelpTag/HelpTag'

/**
 * Component properties type.
 */
type Props = {
    includeCallsForGrpDevEnabled: boolean
    includeCallsForGrpDev: boolean
    setIncludeCallsForGrpDev: (checked: boolean) => void
    calcACDStatsForAgntsSignedIntoSpecGrpEnabled: boolean
    calcACDStatsForAgntsSignedIntoSpecGrp: boolean
    setCalcACDStatsForAgntsSignedIntoSpecGrp: (checked: boolean) => void
    currentTelSysType: number
}

/**
 * Device filtering section in Advanced tab of report configuration modal window.
 * @param includeCallsForGrpDevEnabled Flag specifying whether setting corresponding property is allowed.
 * @param includeCallsForGrpDev Report property.
 * @param setIncludeCallsForGrpDev Setter for corresponding report property.
 * @param calcACDStatsForAgntsSignedIntoSpecGrpEnabled Flag specifying whether setting corresponding property is allowed.
 * @param calcACDStatsForAgntsSignedIntoSpecGrp Report property.
 * @param setCalcACDStatsForAgntsSignedIntoSpecGrp Setter for corresponding report property.
 * @param currentTelSysType Current telephone system type.
 */
const DeviceFiltering: React.FC<Props> = ({
    includeCallsForGrpDevEnabled,
    includeCallsForGrpDev,
    setIncludeCallsForGrpDev,
    calcACDStatsForAgntsSignedIntoSpecGrpEnabled,
    calcACDStatsForAgntsSignedIntoSpecGrp,
    setCalcACDStatsForAgntsSignedIntoSpecGrp,
    currentTelSysType
}: Props): ReactElement => {
    const language = useContext(LanguageContext) as CLanguageHandler
    language.loadBundle('DeviceFiltering')

    return (
        <Fieldset className={'device-filtering'}>
            <Heading level={HeadingLevel.ONE}>
                {language.get(DeviceFilteringLanguage.DeviceFiltering_SectionHeader)}
                <HelpTag
                    className='configure-report-help-title-tag'
                    helpContentTitle={HelpTagTitles.CONFIGURE_REPORT_DEVICE_FILTERING}
                    tag={Tags.CONFIGURE_REPORT}
                />
            </Heading>
            <P>{language.get(DeviceFilteringLanguage.DeviceFiltering_SectionPara)}</P>
            <FormRow isInline className={'include-calls-for-group-devices'}>
                <Checkbox
                    disabled={!includeCallsForGrpDevEnabled}
                    onChange={setIncludeCallsForGrpDev}
                    isChecked={includeCallsForGrpDev}
                    labelLeft={language.get(DeviceFilteringLanguage.IncludecallsforgrpdevmmbrsLabel)}
                />
            </FormRow>
            <FormRow isInline className={'calculate-acd-stats-for-agents'}>
                <Checkbox
                    disabled={!calcACDStatsForAgntsSignedIntoSpecGrpEnabled}
                    onChange={setCalcACDStatsForAgntsSignedIntoSpecGrp}
                    isChecked={calcACDStatsForAgntsSignedIntoSpecGrp}
                    labelLeft={language.getWithMSTCheck(
                        DeviceFilteringLanguage.CalcacdstatsforagentssignedinspecgrpLabel,
                        currentTelSysType
                    )}
                />
            </FormRow>
        </Fieldset>
    )
}

export default React.memo(DeviceFiltering)
